/**
 * INFINITY LIFE 2.0 — API Client with Offline Resilience & Toast Feedback
 */

import { enqueueOfflineOperation, getPendingOperations, clearProcessedOperations, cacheTasks } from './db.js';
import { store } from './state.js';

const API_BASE = '/api';

export function showToast(message, type = 'info', duration = 3500) {
  let container = document.getElementById('toast-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'toast-container';
    container.className = 'toast-container';
    document.body.appendChild(container);
  }

  const toast = document.createElement('div');
  toast.className = `toast toast-${type}`;
  toast.innerHTML = `
    <div class="toast-content">
      <span class="toast-icon">${type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ'}</span>
      <span class="toast-message">${escapeHtml(message)}</span>
    </div>
  `;

  container.appendChild(toast);

  setTimeout(() => {
    toast.classList.add('toast-fade');
    setTimeout(() => toast.remove(), 400);
  }, duration);
}

function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

export async function request(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`;
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  const token = localStorage.getItem('infinity_token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    headers['X-Infinity-Token'] = token;
  }

  const config = {
    ...options,
    headers,
    credentials: 'include',
  };

  try {
    const response = await fetch(url, config);
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const errorMsg = data.error || `Erro HTTP ${response.status}`;
      throw new Error(errorMsg);
    }

    return data;
  } catch (error) {
    // If offline or network error and is a mutation, handle via offline queue
    if (!navigator.onLine || error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      if (options.method && options.method !== 'GET') {
        return handleOfflineMutation(endpoint, options);
      }
    }
    throw error;
  }
}

async function handleOfflineMutation(endpoint, options) {
  const method = options.method.toUpperCase();
  const body = options.body ? JSON.parse(options.body) : {};

  let opType = 'create';
  let opId = null;

  if (endpoint.startsWith('/tasks/')) {
    const parts = endpoint.split('/');
    opId = parseInt(parts[2], 10) || null;

    if (parts[3] === 'toggle') {
      opType = 'toggle';
    } else if (method === 'PUT') {
      opType = 'update';
    } else if (method === 'DELETE') {
      opType = 'delete';
    }
  } else if (endpoint === '/tasks' && method === 'POST') {
    opType = 'create';
  }

  const queued = await enqueueOfflineOperation(opType, body, opId);
  showToast('Alteração salva localmente (Offline). Será sincronizada ao reconectar.', 'info');

  // Update pending sync count
  const pending = await getPendingOperations();
  store.set({ pendingSyncCount: pending.length });

  return {
    success: true,
    offline: true,
    message: 'Salvo localmente.',
    data: { temp_id: queued.temp_id, id: opId || queued.temp_id, ...body },
  };
}

/**
 * Sync all queued offline operations with backend
 */
export async function syncOfflineQueue() {
  if (!navigator.onLine) return;

  const pending = await getPendingOperations();
  if (pending.length === 0) {
    store.set({ pendingSyncCount: 0 });
    return;
  }

  try {
    const response = await fetch(`${API_BASE}/tasks/sync`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('infinity_token') || ''}`,
      },
      credentials: 'include',
      body: JSON.stringify({ operations: pending }),
    });

    if (response.ok) {
      const resData = await response.json();
      const successTempIds = pending.map((p) => p.temp_id);
      await clearProcessedOperations(successTempIds);
      store.set({ pendingSyncCount: 0 });
      showToast('Sincronização concluída com o servidor.', 'success');
      window.dispatchEvent(new CustomEvent('infinity:refresh-data'));
    }
  } catch (err) {
    console.warn('Erro durante sincronização offline:', err);
  }
}

// Global API service methods
export const api = {
  get: (endpoint) => request(endpoint, { method: 'GET' }),
  post: (endpoint, body) => request(endpoint, { method: 'POST', body: JSON.stringify(body) }),
  put: (endpoint, body) => request(endpoint, { method: 'PUT', body: JSON.stringify(body) }),
  patch: (endpoint, body = {}) => request(endpoint, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: (endpoint) => request(endpoint, { method: 'DELETE' }),

  // Auth
  getAuthConfig: () => request('/auth/config'),
  getMe: () => request('/auth/me'),
  googleLogin: (credential) => request('/auth/google', { method: 'POST', body: JSON.stringify({ credential }) }),
  ownerLogin: (email, key) => request('/auth/owner-login', { method: 'POST', body: JSON.stringify({ email, key }) }),
  devLogin: (email, name) => request('/auth/dev-login', { method: 'POST', body: JSON.stringify({ email, name }) }),
  logout: () => request('/auth/logout', { method: 'POST' }),
  updatePreferences: (preferences) => request('/auth/preferences', { method: 'PUT', body: JSON.stringify({ preferences }) }),
  exportData: () => request('/auth/export-data'),
  deleteAccount: (confirmation) => request('/auth/delete-account', { method: 'POST', body: JSON.stringify({ confirmation }) }),

  // Tasks
  getTasks: (params = '') => request(`/tasks${params ? '?' + params : ''}`),
  getTask: (id) => request(`/tasks/${id}`),
  createTask: (data) => request('/tasks', { method: 'POST', body: JSON.stringify(data) }),
  updateTask: (id, data) => request(`/tasks/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  toggleTask: (id) => request(`/tasks/${id}/toggle`, { method: 'PATCH' }),
  deleteTask: (id) => request(`/tasks/${id}`, { method: 'DELETE' }),

  // Dashboard
  getMyDay: () => request('/dashboard/my-day'),

  // Notifications
  getNotifications: () => request('/notifications'),
  markNotificationRead: (id) => request(`/notifications/${id}/read`, { method: 'PATCH' }),
  markAllNotificationsRead: () => request('/notifications/read-all', { method: 'POST' }),
  subscribePush: (subData) => request('/notifications/subscribe-push', { method: 'POST', body: JSON.stringify(subData) }),
  sendTestNotification: () => request('/notifications/test', { method: 'POST' }),

  // Admin
  getAdminOverview: () => request('/admin/overview'),
  getAdminSettings: () => request('/admin/settings'),
  updateAdminSettings: (settings) => request('/admin/settings', { method: 'PUT', body: JSON.stringify({ settings }) }),
  getAdminLogs: () => request('/admin/audit-logs'),

  // Infinity AI (Gemini)
  aiChat: (message) => request('/ai/chat', { method: 'POST', body: JSON.stringify({ message }) }),
};

